import { Eye, User, Ban, CheckCircle } from "lucide-react";
import { ContainerCard, EmployeeDetails, ModuleTitleCard } from "../../components/ui/cards";
import { Reactable } from "../../components/ui/reactable";
import { useState } from "react";
import { EMPLOYEE_INTERN_INFO } from "../../constants/api";
import { checkUserPermissionFunc } from "../../components/common/CommonFunctions";
import dayjs from "dayjs";
import { cn } from "../../utils/tailwindUtils";
import { Button } from "../../components/ui/button";
import { useNavigate } from "react-router-dom";
import { ConfirmPopupModal } from "../../components/ui/dialogModal";
import useApiMethods from "../../lib/useApiMethods";

const InternOnboardingList = () => {
    // states and variables
    const { update } = useApiMethods()
    const [employeeData, setEmployeeData] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [isConfirmOpen, setIsConfirmOpen] = useState(false);
    const [selectedRow, setSelectedRow] = useState(null);
    const [loading, setLoading] = useState(false);
    const [serverTrigger, setServerTrigger] = useState(false);
    const [actionType, setActionType] = useState(''); // 'activate' or 'deactivate'

    const { edit: editPermission, add: addPermission, active: isActive } = checkUserPermissionFunc('employee', 'employees', 'SubMenu');
    let navigate = useNavigate();

    // columns
    const employeeColumns = [
        {
            accessor: "employee_details",
            Header: "Employee",
            sortable: true,
            fixed: true,
            filterable: true,
            width: "250px",
            Cell: ({ value }) => {
                const record = value;

                const empDetails = {
                    // Name handling
                    first_name: record?.employee_name,
                    title: record?.title,

                    // Profile
                    profile: record?.profile,

                    // Designation - handle both structures
                    designation: record?.designation,
                    designation_id: record?.designation ? {
                        designation_name: record.designation.name
                    } : null,

                    // Employee code
                    employee_id: record?.employee_id,
                    id: record?.employee_id || record?.id,

                    // Other fields that might be needed
                    department: record?.department,
                    employee_status: record?.employee_status
                };

                return <EmployeeDetails details={empDetails} size="xs" />;
            },
        },
        {
            accessor: "department",
            Header: "Applied for Department",
            sortable: true,
            filterable: true,
            Cell: ({ row }) => {
                const record = row;
                const department = record?.employee_details.department.name || "-";
                return department;
            },
        },
        {
            accessor: "registered_date",
            Header: "Registered Date",
            sortable: true,
            filterable: true,
            Cell: ({ value }) => {
                if (!value) return "-";

                try {
                    return dayjs(value, "YYYY-MM-DD").format("DD-MM-YYYY");
                } catch (error) {
                    console.error("Date formatting error:", error);
                    return value;
                }
            },
        },
        {
            accessor: "registered_by",
            Header: "Registered By",
            sortable: true,
            filterable: true,
            Cell: ({ value }) => {
                return (
                    value ?? "-"
                )
            },
        },
        {
            accessor: "status",
            Header: "Status",
            sortable: false,
            filterable: true,
            Cell: ({ value }) => value.charAt(0).toUpperCase() + value.slice(1).toLowerCase() ?? '-'
        },
        {
            accessor: "stage",
            Header: "Stage",
            sortable: false,
            filterable: true,
            Cell: ({ value }) => value ?? '-'
        },
        ...(editPermission ? [
            {
                accessor: 'actions',
                Header: 'Actions',
                fixed: true,
                align: 'right',
                Cell: ({ row }) => {
                    const record = row;
                    const isEmployeeActive = Boolean(record.is_active);

                    const handleView = () => {
                        navigate("/intern/onboarding/viewDetails", {
                            state: {
                                row: record,
                                employeeID: record.employee_details.id,
                                isActive: isActive
                            }
                        });
                    };

                    const handleToggleStatus = () => {
                        setSelectedRow(record);
                        setActionType(isEmployeeActive ? 'deactivate' : 'activate');
                        setIsConfirmOpen(true);
                    };

                    return (
                        <div className="flex gap-1 justify-end">
                            <Button
                                variant='ghost'
                                onClick={handleView}
                                className="px-3 py-2 hover:bg-orange-50 dark:hover:bg-orange-900/20"
                                title="View Employee Details"
                            >
                                <Eye className="text-orange-500 h-4 w-4" />
                            </Button>
                        </div>
                    );
                }
            }
        ] : [])
    ];

    const handleAddEmployee = () => {
        navigate('/intern/add');
    };

    const handleConfirmToggle = async () => {
        if (!selectedRow) return;

        setLoading(true);
        try {
            const employeeId = selectedRow.id;
            const newStatus = actionType === 'activate';

            const payload = {
                employee_id: employeeId,
                is_active: newStatus,
            };

            await update(EMPLOYEE_INTERN_INFO, payload);

            // Update local state immediately for better UX
            setEmployeeData(prevData =>
                prevData.map(employee =>
                    (employee.employee_id === employeeId || employee.id === employeeId)
                        ? { ...employee, is_active: newStatus }
                        : employee
                )
            );

            setServerTrigger(true)

        } catch (error) {
            console.error(`Error ${actionType}ing employee:`, error);
        } finally {
            setLoading(false);
            setIsConfirmOpen(false);
            setSelectedRow(null);
            setActionType('');
            setServerTrigger(false)
        }
    };

    const getModalConfig = () => {
        const isActivating = actionType === 'activate';
        return {
            title: isActivating ? "Activate Employee" : "Deactivate Employee",
            description: isActivating
                ? "Are you sure you want to activate this employee? They will regain access to the system."
                : "Are you sure you want to deactivate this employee? They will no longer have access to the system.",
            confirmlabel: isActivating ? "Activate" : "Deactivate",
            type: isActivating ? "success" : "warning"
        };
    };

    const modalConfig = getModalConfig();

    return (
        <ContainerCard>
            <ModuleTitleCard
                Icon={User}
                title={'Intern Onboarding'}
                subtitle={'Manage onboarding intern candidate list and their details'}
                buttonEnable={!!addPermission}
                buttonName="Add Employee"
                totalRecords={totalRecords}
                setOpenDialog={handleAddEmployee}
            />

            <Reactable
                data={employeeData}
                setData={setEmployeeData}
                columns={employeeColumns}
                serverSide
                url={EMPLOYEE_INTERN_INFO}
                setTotalCount={setTotalRecords}
                serverTrigger={serverTrigger}
                tableHeight="calc(100vh - 320px)"
                rowSize={editPermission ? "sm" : null}
                isCSVRequired
                isPDFRequired
            />

            <ConfirmPopupModal
                open={isConfirmOpen}
                onClose={() => {
                    setIsConfirmOpen(false);
                    setSelectedRow(null);
                    setActionType('');
                }}
                onConfirm={handleConfirmToggle}
                title={modalConfig.title}
                description={modalConfig.description}
                type={modalConfig.type}
                confirmlabel={modalConfig.confirmlabel}
                destructive={actionType === 'deactivate'}
                loading={loading}
            />
        </ContainerCard>
    );
};

export default InternOnboardingList;